import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

// Authentication & Dashboard
import LoginForm from './components/admin/LoginForm';
import Dashboard from './components/admin/Dashboard';
import GlobalCheckinNotifier from './components/admin/GlobalCheckinNotifier';
import Logout from './components/admin/Logout';

// Module Routes
import Modules from './components/admin/Modules';
import AddModule from './components/admin/AddModule';
import EditModule from './components/admin/EditModule';

// Outlet Routes
import Outlets from './components/admin/Outlets';
import AddOutlet from './components/admin/AddOutlet';
import EditOutlet from './components/admin/EditOutlet';
import OutletModules from './components/admin/OutletModules';
import OutletModuleForm from './components/admin/OutletModuleForm';

// Employee (Karyawan) Routes
import Employees from './components/admin/Employees';
import KaryawanForm from './components/admin/KaryawanForm';
import EditKaryawanForm from './components/admin/EditKaryawanForm';
import KaryawanModules from './components/admin/KaryawanModules';
import KaryawanModulesForm from './components/admin/KaryawanModulesForm';

// Member Routes
import Members from './components/admin/Members';
import MemberForm from './components/admin/MemberForm';
import MembershipPayments from './components/admin/MembershipPayments';
import MembershipPaymentStatistics from './components/admin/MembershipPaymentStatistics';

// Membership Routes
import Memberships from './components/admin/Memberships';
import MembershipForm from './components/admin/MembershipForm';
import MemberModules from './components/admin/MemberModules';         // ✅ Added MemberModules
import MemberModuleForm from './components/admin/MemberModuleForm';
import DefaultKaryawanModules from './components/admin/DefaultKaryawanModules';   // ✅ Added MemberModuleForm
import AddEditKaryawanModules from './components/admin/AddEditKaryawanModules';
import DefaultMemberModules from './components/admin/DefaultMemberModules';
import AddEditDefaultMemberModules from './components/admin/AddEditDefaultMemberModules';
import ModuleQrCode from './components/admin/ModuleQRCode';
import Missions from './components/admin/Missions';
import InsertMission from './components/admin/InsertMission';
import EditMission from './components/admin/EditMission';
import Rewards from './components/admin/Rewards';
import AddReward from './components/admin/Addrewards';
import Deposits from './components/admin/Deposits';
import Editdeposit from './components/admin/Editdeposit';
import Balance from './components/admin/Balance';
import AccountingSetup from './components/admin/AccountingSetup';
import FitnessFinanceManager from './components/admin/FitnessFinanceManager';
import AssetManagementManager from './components/admin/AssetManagementManager';
import PosCategoriesManager from './components/admin/PosCategoriesManager';
import PosProductsManager from './components/admin/PosProductsManager';
import PosItemsManager from './components/admin/PosItemsManager';
import PosCustomersManager from './components/admin/PosCustomersManager';
import PosLaundryReferenceManager from './components/admin/PosLaundryReferenceManager';
import PosInventoryDocumentManager from './components/admin/PosInventoryDocumentManager';
import PosStockAdjustmentApprovalManager from './components/admin/PosStockAdjustmentApprovalManager';
import PosVendorManager from './components/admin/PosVendorManager';
import PosAccountMappings from './components/admin/PosAccountMappings';
import PosSyncMonitor from './components/admin/PosSyncMonitor';
import PosApprovalManager from './components/admin/PosApprovalManager';
import CheckinReport from './components/admin/CheckinReports';
import ReportCenter from './components/admin/ReportCenter';
import StaffTransactionSummaryPage from './components/admin/StaffTransactionSummaryPage';
import MemberActivityMonitorPage from './components/admin/MemberActivityMonitorPage';
import StaffShiftClosingPage from './components/admin/StaffShiftClosingPage';
import PTPhase15Manager from './components/admin/PTPhase15Manager';
import PTScheduleManager from './components/admin/PTScheduleManager';
import FitnessTransactionsManager from './components/admin/FitnessTransactionsManager';
import FitnessTransactionSetup from './components/admin/FitnessTransactionSetup';

//membershippayment 
import 'bootstrap/dist/css/bootstrap.min.css';
import useAuth from './hooks/useAuth';
import { hasAccessToPath, readStoredAdminUser } from './components/admin/adminAccessControl';
import { clearAdminSession, readStoredSessionToken } from './utils/adminSession';

function ProtectedAdminRoute({ element }) {
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let isMounted = true;

    const validateAccess = async () => {
      const token = readStoredSessionToken();
      if (!token) {
        if (isMounted) {
          setStatus('unauthenticated');
        }
        return;
      }

      const isValid = await verifyAndRefreshToken(token);
      if (!isMounted) {
        return;
      }

      if (!isValid) {
        clearAdminSession();
        setStatus('unauthenticated');
        return;
      }

      const userData = readStoredAdminUser();
      setStatus(hasAccessToPath(location.pathname, userData) ? 'allowed' : 'forbidden');
    };

    validateAccess();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, verifyAndRefreshToken]);

  if (status === 'checking') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-body-tertiary">
        <div className="text-center">
          <Spinner animation="border" />
          <div className="mt-3">Memeriksa akses...</div>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/loginadmin" replace state={{ from: location }} />;
  }

  if (status === 'forbidden') {
    return <Navigate to="/dashboardadmin" replace />;
  }

  return element;
}

function LegacyFinanceRedirect() {
  const location = useLocation();
  return <Navigate to={`/operasional-finance${location.search || ''}`} replace />;
}

function App() {
  useEffect(() => {
    document.title = "Portal Administrasi Outlet";
  }, []);

  return (
    <Router>
      <div className="App">
        <GlobalCheckinNotifier />
        <Routes>
          {/* Authentication Routes */}
          <Route path="/" element={<LoginForm />} />
          <Route path="/loginadmin" element={<LoginForm />} />
          <Route path="/logout" element={<Logout />} />

          {/* Dashboard */}
          <Route path="/dashboardadmin" element={<ProtectedAdminRoute element={<Dashboard />} />} />
          <Route path="/deposits" element={<ProtectedAdminRoute element={<Deposits />} />} />

          {/* Module Routes */}
          <Route path="/modules" element={<ProtectedAdminRoute element={<Modules />} />} />
          <Route path="/addmodule" element={<ProtectedAdminRoute element={<AddModule />} />} />
          <Route path="/editmodule/:id" element={<ProtectedAdminRoute element={<EditModule />} />} />

          {/* Outlet Routes */}
          <Route path="/outlets" element={<ProtectedAdminRoute element={<Outlets />} />} />
          <Route path="/addoutlet" element={<ProtectedAdminRoute element={<AddOutlet />} />} />
          <Route path="/editoutlet/:id" element={<ProtectedAdminRoute element={<EditOutlet />} />} />
          <Route path="/outlet-modules" element={<ProtectedAdminRoute element={<OutletModules />} />} />
          <Route path="/add-outlet-module" element={<ProtectedAdminRoute element={<OutletModuleForm />} />} />
          <Route path="/edit-outlet-module/:id" element={<ProtectedAdminRoute element={<OutletModuleForm />} />} />

          {/* Employee Routes */}
          <Route path="/employees" element={<ProtectedAdminRoute element={<Employees />} />} />
          <Route path="/add-employee" element={<ProtectedAdminRoute element={<KaryawanForm />} />} />
          <Route path="/edit-employee/:id" element={<ProtectedAdminRoute element={<EditKaryawanForm />} />} />
          <Route path="/karyawanmodules" element={<ProtectedAdminRoute element={<KaryawanModules />} />} />
          <Route path="/add-karyawan-module" element={<ProtectedAdminRoute element={<KaryawanModulesForm />} />} />
          <Route path="/edit-karyawan-module/:id" element={<ProtectedAdminRoute element={<KaryawanModulesForm />} />} />

          {/* Member Routes */}
          <Route path="/members" element={<ProtectedAdminRoute element={<Members />} />} />
          <Route path="/add-member" element={<ProtectedAdminRoute element={<MemberForm />} />} />
          <Route path="/edit-member/:id" element={<ProtectedAdminRoute element={<MemberForm />} />} />
          <Route path="/member-modules" element={<ProtectedAdminRoute element={<MemberModules />} />} />
          <Route path="/add-member-module" element={<ProtectedAdminRoute element={<MemberModuleForm />} />} />
          <Route path="/edit-member-module/:id" element={<ProtectedAdminRoute element={<MemberModuleForm />} />} />   

          {/* Membership Routes */}
          <Route path="/membership-payment" element={<ProtectedAdminRoute element={<MembershipPayments />} />} />
          <Route
            path="/membership-daily-stats"
            element={<ProtectedAdminRoute element={<MembershipPaymentStatistics view="daily_member" />} />}
          />
          <Route
            path="/membership-monthly-stats"
            element={<ProtectedAdminRoute element={<MembershipPaymentStatistics view="monthly_membership" />} />}
          />
          <Route path="/membership-setting" element={<ProtectedAdminRoute element={<Memberships />} />} />
          <Route path="/edit-membership/:id" element={<ProtectedAdminRoute element={<MembershipForm />} />} />
          <Route path="/add-membership" element={<ProtectedAdminRoute element={<MembershipForm />} />} />
          <Route path="/defaultmodulekaryawan" element={<ProtectedAdminRoute element={<DefaultKaryawanModules />} />} />
          <Route path="/add-default-karyawan-module" element={<ProtectedAdminRoute element={<AddEditKaryawanModules />} />} />
          <Route path="/edit-default-karyawan-module/:id" element={<ProtectedAdminRoute element={<AddEditKaryawanModules />} />} />

          <Route path="/default-member-modules" element={<ProtectedAdminRoute element={<DefaultMemberModules />} />} />
          <Route path="/add-default-member-module" element={<ProtectedAdminRoute element={<AddEditDefaultMemberModules />} />} />
          <Route path="/edit-default-member-module/:id" element={<ProtectedAdminRoute element={<AddEditDefaultMemberModules />} />} />
          <Route path="/showqr" element={<ProtectedAdminRoute element={<ModuleQrCode />} />} />
          <Route path="/missions" element={<ProtectedAdminRoute element={<Missions />} />} />
          <Route path="/addmission" element={<ProtectedAdminRoute element={<InsertMission />} />} />
          <Route path="/editmission/:id" element={<ProtectedAdminRoute element={<EditMission />} />} />
          <Route path="/rewards" element={<ProtectedAdminRoute element={<Rewards />} />} />
          <Route path="/addreward" element={<ProtectedAdminRoute element={<AddReward />} />} />
          <Route path="/deposit/edit/:id" element={<ProtectedAdminRoute element={<Editdeposit />} />} />
          <Route path="/user-balance" element={<ProtectedAdminRoute element={<Balance />} />} />
          <Route path="/accounting-setup" element={<ProtectedAdminRoute element={<AccountingSetup />} />} />
          <Route path="/fitness-finance" element={<LegacyFinanceRedirect />} />
          <Route path="/operasional-finance" element={<ProtectedAdminRoute element={<FitnessFinanceManager />} />} />
          <Route path="/asset-management" element={<ProtectedAdminRoute element={<AssetManagementManager />} />} />
          <Route path="/pos-categories" element={<ProtectedAdminRoute element={<PosCategoriesManager />} />} />
          <Route path="/pos-products" element={<ProtectedAdminRoute element={<PosProductsManager />} />} />
          <Route path="/pos-customers" element={<ProtectedAdminRoute element={<PosCustomersManager />} />} />
          <Route path="/pos-items" element={<ProtectedAdminRoute element={<PosItemsManager />} />} />
          <Route path="/pos-laundry-methods" element={<ProtectedAdminRoute element={<PosLaundryReferenceManager referenceType="metode" />} />} />
          <Route path="/pos-laundry-times" element={<ProtectedAdminRoute element={<PosLaundryReferenceManager referenceType="waktu" />} />} />
          <Route path="/pos-purchases" element={<ProtectedAdminRoute element={<PosInventoryDocumentManager mode="purchase" />} />} />
          <Route path="/pos-stock-adjustments" element={<ProtectedAdminRoute element={<PosStockAdjustmentApprovalManager />} />} />
          <Route path="/pos-stock-opnames" element={<ProtectedAdminRoute element={<PosInventoryDocumentManager mode="stock-opname" />} />} />
          <Route path="/pos-pengajuan-pembatalan" element={<ProtectedAdminRoute element={<PosApprovalManager mode="request" />} />} />
          <Route path="/pos-approval-pembatalan" element={<ProtectedAdminRoute element={<PosApprovalManager mode="approval" />} />} />
          <Route path="/pos-refund-void-approval" element={<ProtectedAdminRoute element={<Navigate to="/pos-pengajuan-pembatalan" replace />} />} />
          <Route path="/pos-vendors" element={<ProtectedAdminRoute element={<PosVendorManager />} />} />
          <Route path="/pos-account-mappings" element={<ProtectedAdminRoute element={<PosAccountMappings />} />} />
          <Route path="/pos-sync-monitor" element={<ProtectedAdminRoute element={<PosSyncMonitor />} />} />
          <Route path="/report-center" element={<ProtectedAdminRoute element={<ReportCenter />} />} />
          <Route path="/staff-transaction-summary" element={<ProtectedAdminRoute element={<StaffTransactionSummaryPage />} />} />
          <Route path="/member-activity-monitor" element={<ProtectedAdminRoute element={<MemberActivityMonitorPage />} />} />
          <Route path="/staff-shift-closing" element={<ProtectedAdminRoute element={<StaffShiftClosingPage />} />} />
          <Route path="/add-membershippayment" element={<ProtectedAdminRoute element={<Navigate to="/fitness-transactions?tab=membership" replace />} />} />
          <Route path="/reportcheckin" element={<ProtectedAdminRoute element={<CheckinReport />} />} />
          <Route path="/pt-schedule-setting" element={<ProtectedAdminRoute element={<PTScheduleManager />} />} />
          <Route path="/fitness-transactions" element={<ProtectedAdminRoute element={<FitnessTransactionsManager />} />} />
          <Route path="/fitness-transactions/setup" element={<ProtectedAdminRoute element={<FitnessTransactionSetup />} />} />
          <Route path="/fitness-transactions/setup/pt-setting" element={<ProtectedAdminRoute element={<PTPhase15Manager />} />} />
          <Route path="/session-clients" element={<ProtectedAdminRoute element={<PTPhase15Manager pageMode="clients-only" />} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
